import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import StoreBadges from "@/components/feature/StoreBadges";
import Logo from "@/components/feature/Logo";
import { track } from "@/lib/analytics";

const NAV_LINKS = [
  { label: "Products", href: "/products" },
  { label: "Industries", href: "#industries" },
  { label: "How it works", href: "#how" },
  { label: "FAQ", href: "#faq" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/85 backdrop-blur-md border-b border-background-300/25"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="w-full px-4 md:px-8">
        <div className="flex h-16 md:h-20 items-center justify-between">
          <a href="#top" className="flex items-center cursor-pointer">
            <Logo imgClassName="h-7 md:h-8" />
          </a>

          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((link) =>
              link.href.startsWith("/") ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {link.label}
                </a>
              )
            )}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://app.joble.app/auth/get-started"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("signin_clicked", { location: "navbar_desktop" })}
              className="text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap px-3 py-2"
            >
              Sign in
            </a>
            <a
              href="https://app.joble.app/auth/create-account?free=true"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("try_joble_clicked", { location: "navbar_desktop" })}
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 text-foreground-950 text-sm font-semibold px-5 py-2.5 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Try Joble Now
              <i className="ri-arrow-right-line" aria-hidden></i>
            </a>
          </div>

          <button
            type="button"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-950 cursor-pointer"
          >
            <i className={open ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} aria-hidden></i>
          </button>
        </div>

        {open && (
          <div className="md:hidden pb-6 pt-2 animate-fadeIn">
            <div className="flex flex-col gap-1">
              {NAV_LINKS.map((link) =>
                link.href.startsWith("/") ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setOpen(false)}
                    className="text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors py-2.5 cursor-pointer"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => setOpen(false)}
                    className="text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors py-2.5 cursor-pointer"
                  >
                    {link.label}
                  </a>
                )
              )}
            </div>
            <div className="flex flex-col gap-3 pt-4">
              <a
                href="https://app.joble.app/auth/get-started"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("signin_clicked", { location: "navbar_mobile_menu" })}
                className="text-center rounded-md border border-background-300/50 text-foreground-900 text-sm font-semibold px-5 py-3 cursor-pointer"
              >
                Sign in
              </a>
              <a
                href="https://app.joble.app/auth/create-account?free=true"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("try_joble_clicked", { location: "navbar_mobile_menu" })}
                className="text-center rounded-md bg-primary-500 text-foreground-950 text-sm font-semibold px-5 py-3 cursor-pointer"
              >
                Try Joble Now
              </a>
            </div>
            <div className="mt-6 pt-5 border-t border-background-300/30">
              <span className="text-xs font-semibold text-foreground-500 uppercase tracking-[0.15em] mb-3 block">
                Get the app
              </span>
              <StoreBadges justify="start" direction="col" fullWidth />
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}